import { Card } from "@/components/flexkit/Card";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Switch } from "@/components/Switch";
import { useGetCustomDomain, useGetEnforceDomainPolicy, useSetEnforceDomainPolicy } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { type FC, useMemo, useState } from "react";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useCanonicalDomainRunners, type RunnerWithUsage } from "@/hooks/use-canonical-domain-runners";
import { EnforceCustomDomainConfirmationDialog } from "./EnforceCustomDomainConfirmationDialog";
import type { PlainRunner } from "@/queries/runner-queries";

type EnforceCustomDomainCardProps = {
    organizationId: string;
    editable?: boolean;
};

export const EnforceCustomDomainCard: FC<EnforceCustomDomainCardProps> = ({ organizationId, editable = true }) => {
    const { toast } = useToast();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingEnforcement, setPendingEnforcement] = useState(false);

    const { data: customDomain, isLoading: isDomainLoading } = useGetCustomDomain(organizationId);
    const { data: policy, isLoading: isPolicyLoading } = useGetEnforceDomainPolicy(organizationId);
    const setPolicy = useSetEnforceDomainPolicy();

    // Determine the canonical domain - this is the default Gitpod domain (app.gitpod.io)
    const canonicalDomain = useMemo(() => {
        return "app.gitpod.io";
    }, []);

    // Fetch runners using canonical domain
    const { runners: canonicalRunners, isLoading: isLoadingRunners } = useCanonicalDomainRunners(canonicalDomain);

    const isLoading = isDomainLoading || isPolicyLoading;
    const hasDomain = !!customDomain;
    const isEnforced = policy?.enforced ?? false;

    // ARIA live region message for screen readers
    const ariaLiveMessage = useMemo(() => {
        if (setPolicy.isPending) {
            return "Updating domain enforcement policy...";
        }
        if (setPolicy.isSuccess) {
            return isEnforced ? "Domain enforcement enabled" : "Domain enforcement disabled";
        }
        return "";
    }, [setPolicy.isPending, setPolicy.isSuccess, isEnforced]);

    const handleToggle = async (checked: boolean) => {
        // If enabling enforcement, check for affected runners
        if (checked && !isLoadingRunners && canonicalRunners.length > 0) {
            setPendingEnforcement(true);
            setShowConfirmDialog(true);
            return;
        }

        // If disabling or no affected runners, proceed directly
        await performToggle(checked);
    };

    const performToggle = async (checked: boolean) => {
        try {
            await setPolicy.mutateAsync({
                organizationId,
                enforced: checked,
            });
            toast({
                title: checked ? "Domain enforcement enabled" : "Domain enforcement disabled",
            });
            setShowConfirmDialog(false);
            setPendingEnforcement(false);
        } catch (error) {
            toast({
                title: "Failed to update enforcement policy",
                description: formatError(error),
                variant: "default",
            });
            setPendingEnforcement(false);
        }
    };

    const handleConfirm = () => {
        void performToggle(true);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
        setPendingEnforcement(false);
    };

    if (isLoading) {
        return (
            <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
                <div className="flex gap-4">
                    <div className="flex w-11 items-center justify-center">
                        <SkeletonBlock ready={false} className="h-6 w-11" />
                    </div>
                    <div className="flex flex-1 flex-col gap-3">
                        <p className="text-base font-normal text-content-primary">Enforce custom domain at login</p>
                        <SkeletonBlock ready={false} className="h-10 w-full" />
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
                {/* ARIA live region for screen reader announcements */}
                <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                    {ariaLiveMessage}
                </div>
                <div className="flex gap-4">
                    <div className="flex w-11 items-center justify-center">
                        <Switch
                            state={setPolicy.isPending || pendingEnforcement ? "pending" : isEnforced ? "checked" : "unchecked"}
                            onToggle={(checked) => void handleToggle(checked)}
                            disabled={!hasDomain || !editable}
                            id="enforce-custom-domain-toggle"
                            aria-label="Enforce custom domain at login"
                        />
                    </div>
                    <div className="flex flex-1 flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <p className="text-base font-normal text-content-primary">Enforce custom domain at login</p>
                            <span className="text-sm text-content-secondary">
                                {!hasDomain
                                    ? "Configure a custom domain first to enable enforcement"
                                    : "When enabled, users must access your organization through your custom domain. Both web login and API access (including personal access tokens) via the canonical domain will be blocked for non-admin users."}
                            </span>
                        </div>
                    </div>
                </div>
            </Card>

            {showConfirmDialog && (
                <EnforceCustomDomainConfirmationDialogWrapper
                    isOpen={showConfirmDialog}
                    onClose={handleCancel}
                    onConfirm={handleConfirm}
                    runners={canonicalRunners}
                    canonicalDomain={canonicalDomain}
                    isLoading={setPolicy.isPending}
                />
            )}
        </>
    );
};

// Wrapper component to fetch usage stats for runners when dialog opens
const EnforceCustomDomainConfirmationDialogWrapper: FC<{
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    runners: PlainRunner[];
    canonicalDomain: string;
    isLoading: boolean;
}> = ({ isOpen, onClose, onConfirm, runners, canonicalDomain, isLoading }) => {
    // Map runners to include usage stats
    // The actual usage fetching happens in the RunnerUsageCard component
    const runnersWithUsage: RunnerWithUsage[] = useMemo(
        () =>
            runners.map((runner) => ({
                runner,
                projectCount: 0, // Will be fetched in RunnerUsageCard
                environmentCount: 0, // Will be fetched in RunnerUsageCard
                hasMoreEnvironments: false, // Will be fetched in RunnerUsageCard
            })),
        [runners],
    );

    return (
        <EnforceCustomDomainConfirmationDialog
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
            runners={runnersWithUsage}
            canonicalDomain={canonicalDomain}
            isLoading={isLoading}
        />
    );
};
